/* eslint-disable indent */
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  ListSubheader,
  Collapse,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandLess,
  ExpandMore,
  HorizontalRule as HLineIcon,
  VerticalAlignCenter as VLineIcon,
  TextFields as TextIcon,
  ShowChart as TrendlineIcon,
  Timeline as RayIcon,
  CropSquare as RectangleIcon,
  EmojiEmotions as EmojiIcon,
  SwapHoriz as ChannelIcon,
  Functions as FibIcon,
  Straighten as MeasureIcon,
  NotificationsActive as AlertIcon,
  Brush as BrushIcon,
  CallMade as ArrowIcon,
  Add as CrossIcon,
  DateRange as TimeRangeIcon,
  Architecture as PitchforkIcon,
  TrendingUp as LongPositionIcon,
  TrendingDown as ShortPositionIcon,
} from '@mui/icons-material';

/**
 * Get icon for annotation type
 */
const getAnnotationIcon = (type) => {
  const iconMap = {
    hline: HLineIcon,
    vline: VLineIcon,
    crossline: CrossIcon,
    text: TextIcon,
    trendline: TrendlineIcon,
    ray: RayIcon,
    rectangle: RectangleIcon,
    emoji: EmojiIcon,
    channel: ChannelIcon,
    fibRetracement: FibIcon,
    measure: MeasureIcon,
    alert_line: AlertIcon,
    brush: BrushIcon,
    arrow: ArrowIcon,
    timerange: TimeRangeIcon,
    pitchfork: PitchforkIcon,
    long_position: LongPositionIcon,
    short_position: ShortPositionIcon,
    anchored_vwap: TrendlineIcon,
  };
  return iconMap[type] || HLineIcon;
};

/**
 * Get display name for annotation type
 */
const getTypeDisplayName = (type) => {
  const nameMap = {
    hline: 'Horizontal Lines',
    vline: 'Vertical Lines',
    crossline: 'Cross Lines',
    text: 'Text Labels',
    trendline: 'Trend Lines',
    ray: 'Rays',
    rectangle: 'Rectangles',
    emoji: 'Emojis',
    channel: 'Channels',
    fibRetracement: 'Fibonacci',
    measure: 'Measurements',
    alert_line: 'Alert Lines',
    brush: 'Drawings',
    arrow: 'Arrows',
    timerange: 'Time Ranges',
    pitchfork: 'Pitchforks',
    long_position: 'Long Positions',
    short_position: 'Short Positions',
    anchored_vwap: 'Anchored VWAP',
  };
  return nameMap[type] || type;
};

/**
 * Get summary text for an annotation
 * Note: Annotations from useChartAnnotations are flattened (no nested data object)
 */
const getAnnotationSummary = (annotation) => {
  const { type } = annotation;
  
  switch (type) {
    case 'hline':
      return annotation.label || (annotation.price ? `$${annotation.price.toFixed(2)}` : '?');
    case 'vline':
      return annotation.label || (annotation.time ? new Date(annotation.time).toLocaleDateString() : '?');
    case 'crossline':
      return annotation.label || (annotation.price ? `$${annotation.price.toFixed(2)}` : '?');
    case 'text':
      return (annotation.text || '').slice(0, 30) || 'Text';
    case 'trendline':
    case 'ray':
      return annotation.label || 'Line';
    case 'rectangle':
      return annotation.label || 'Rectangle';
    case 'emoji':
      return annotation.emoji || '😀';
    case 'channel':
      return annotation.label || 'Channel';
    case 'fibRetracement':
      return annotation.label || 'Fib Retracement';
    case 'measure':
      return 'Measurement';
    case 'alert_line':
      return annotation.label || (annotation.price ? `Alert $${annotation.price.toFixed(2)}` : 'Alert ?');
    case 'brush':
      return 'Drawing';
    case 'arrow':
      return annotation.text || annotation.label || 'Arrow';
    case 'timerange':
      return annotation.label || 'Time Range';
    case 'pitchfork':
      return annotation.label || 'Pitchfork';
    case 'long_position':
      return annotation.label || `Long @ ${annotation.entryPrice?.toFixed(2) ?? '?'}`;
    case 'short_position':
      return annotation.label || `Short @ ${annotation.entryPrice?.toFixed(2) ?? '?'}`;
    case 'anchored_vwap':
      return annotation.label
        || (annotation.anchorTime ? `Anchored ${new Date(annotation.anchorTime).toLocaleDateString()}` : 'Anchored VWAP');
    default:
      return type;
  }
};

/**
 * Panel showing list of annotations grouped by type
 */
const AnnotationListPanel = ({
  annotations = [],
  selectedAnnotationId,
  onSelectAnnotation,
  onToggleVisibility,
  collapsedTypes,
  onToggleTypeCollapse,
}) => {
  // Group annotations by type
  const groupedAnnotations = useMemo(() => {
    const groups = {};
    
    annotations.forEach((annotation) => {
      const { type } = annotation;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(annotation);
    });
    
    // Sort groups by type name and return as array
    return Object.entries(groups)
      .sort(([a], [b]) => getTypeDisplayName(a).localeCompare(getTypeDisplayName(b)))
      .map(([type, items]) => ({
        type,
        displayName: getTypeDisplayName(type),
        annotations: items,
      }));
  }, [annotations]);

  const handleAnnotationClick = useCallback((annotation, event) => {
    // Don't select if clicking on visibility toggle
    if (event.target.closest('.visibility-toggle')) {
      return;
    }
    onSelectAnnotation?.(annotation);
  }, [onSelectAnnotation]);

  const handleVisibilityClick = useCallback((annotation, event) => {
    event.stopPropagation();
    onToggleVisibility?.(annotation);
  }, [onToggleVisibility]);

  const handleTypeToggle = useCallback((type) => {
    onToggleTypeCollapse?.(type);
  }, [onToggleTypeCollapse]);

  if (annotations.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
          px: 2,
          py: 4,
        }}
      >
        <Typography variant="body2" textAlign="center">
          No annotations on this chart.
          <br />
          Use the toolbar to add drawings.
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {groupedAnnotations.map(({ type, displayName, annotations: typeAnnotations }) => {
        const TypeIcon = getAnnotationIcon(type);
        const isCollapsed = collapsedTypes?.has(type);
        
        return (
          <React.Fragment key={type}>
            <ListSubheader
              disableSticky
              onClick={() => handleTypeToggle(type)}
              sx={{
                backgroundColor: 'background.paper',
                color: 'text.secondary',
                px: 2,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                  backgroundColor: (theme) => (theme.vars || theme).palette.action.hover,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TypeIcon fontSize="inherit" />
                <span>{displayName}</span>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: 'text.disabled', ml: 0.5 }}
                >
                  ({typeAnnotations.length})
                </Typography>
              </Box>
              {isCollapsed ? (
                <ExpandMore fontSize="inherit" />
              ) : (
                <ExpandLess fontSize="inherit" />
              )}
            </ListSubheader>
            <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
              {typeAnnotations.map((annotation) => {
                const isSelected = annotation.id === selectedAnnotationId;
                const isHidden = annotation.hidden === true;
                const summary = getAnnotationSummary(annotation);
                
                return (
                  <ListItemButton
                    key={annotation.id}
                    selected={isSelected}
                    onClick={(e) => handleAnnotationClick(annotation, e)}
                    sx={{
                      pl: 3,
                      opacity: isHidden ? 0.5 : 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          backgroundColor: annotation.color || '#888',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={isSelected ? 700 : 500}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textDecoration: isHidden ? 'line-through' : 'none',
                          }}
                        >
                          {summary}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        className="visibility-toggle"
                        onClick={(e) => handleVisibilityClick(annotation, e)}
                        sx={{ 
                          opacity: 0.6,
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        {isHidden ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                );
              })}
            </Collapse>
          </React.Fragment>
        );
      })}
    </List>
  );
};

AnnotationListPanel.propTypes = {
  /** Flat annotations array (fields are at top level, not nested in data) */
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      color: PropTypes.string,
      hidden: PropTypes.bool,
    })
  ),
  selectedAnnotationId: PropTypes.string,
  onSelectAnnotation: PropTypes.func,
  onToggleVisibility: PropTypes.func,
  collapsedTypes: PropTypes.instanceOf(Set),
  onToggleTypeCollapse: PropTypes.func,
};

export default AnnotationListPanel;
